/**
 * Admin → Persona create/edit form (§27.5, Phase 27.2).
 *
 * Two modes:
 *   - /admin/personas/new        — empty form, operator supplies id
 *   - /admin/personas/:id/edit   — loaded from API, id pinned
 *
 * The form is a single JSON textarea covering the structured persona
 * fields plus the assembled `systemPrompt`. The backend validates the
 * structured fields against personas/schema.json and stores systemPrompt
 * verbatim (option 1A — operator authors the assembled prompt directly
 * rather than regenerating it from template slots).
 *
 * Kept intentionally minimal: no live schema preview, no side-by-side
 * diff. An internal admin panel for <50 personas doesn't need more than
 * a syntax-checked JSON editor and a clear error surface.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createAdminPersona,
  getAdminPersona,
  updateAdminPersona,
} from "../services/admin.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

// Only these keys round-trip through the form. DynamoDB internals (PK, SK)
// are stripped before edit and re-added server-side.
const EDITABLE_KEYS = [
  "id",
  "name",
  "category",
  "identity",
  "frameworks",
  "communicationStyle",
  "vocabulary",
  "boundaries",
  "responseFormat",
  "domainExpertise",
  "avatar",
  "agentEnabled",
  "tools",
  "additionalInstructions",
  "enabled",
  "version",
  "systemPrompt",
];

const EMPTY_TEMPLATE = {
  id: "",
  name: "",
  category: "Tech / Business",
  identity: { title: "", era: "", positioning: "" },
  frameworks: [
    { name: "", description: "" },
    { name: "", description: "" },
    { name: "", description: "" },
  ],
  communicationStyle: "",
  vocabulary: [],
  boundaries: "",
  responseFormat: "",
  domainExpertise: { deep: [], defers: [] },
  enabled: true,
  version: 1,
  agentEnabled: false,
  systemPrompt: "",
};

function pick(obj, keys) {
  const out = {};
  for (const k of keys) if (k in obj) out[k] = obj[k];
  return out;
}

export default function AdminPersonaEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [source, setSource] = useState(() =>
    JSON.stringify(EMPTY_TEMPLATE, null, 2),
  );
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    getAdminPersona(id)
      .then((data) => {
        if (cancelled) return;
        const trimmed = pick(data.persona || {}, EDITABLE_KEYS);
        setSource(JSON.stringify(trimmed, null, 2));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  async function handleSave() {
    setSaveError(null);
    let payload;
    try {
      payload = JSON.parse(source);
    } catch (err) {
      setSaveError(`Invalid JSON: ${err.message}`);
      return;
    }
    if (typeof payload !== "object" || payload === null) {
      setSaveError("Payload must be a JSON object");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await createAdminPersona(payload);
        navigate("/admin/personas");
      } else {
        await updateAdminPersona(id, payload);
        navigate("/admin/personas");
      }
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>{isNew ? "New persona" : `Edit ${id}`}</h1>
        <Link to="/admin/personas" className="btn btn-ghost">
          Back
        </Link>
      </div>

      {loadError && <ErrorBubble message={loadError} />}
      {saveError && <ErrorBubble message={saveError} />}

      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : (
        <>
          <p className="admin-page__hint">
            Structured fields are validated against <code>personas/schema.json</code>.
            The <code>systemPrompt</code> is stored verbatim — edit the
            assembled prompt directly.
          </p>
          <textarea
            className="admin-json-editor"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            spellCheck={false}
            rows={30}
          />
          <div className="admin-page__actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
